import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { normalizeSectorName } from "../utils/normalizeSectorName";

// MultiPolygon‑safe centroid
function getFeatureCenter(feature) {
  try {
    const geom = feature.geometry;

    if (geom.type === "Polygon") {
      const coords = geom.coordinates[0];
      const lats = coords.map(c => c[1]);
      const lngs = coords.map(c => c[0]);
      return [
        (Math.min(...lats) + Math.max(...lats)) / 2,
        (Math.min(...lngs) + Math.max(...lngs)) / 2,
      ];
    }

    if (geom.type === "MultiPolygon") {
      const flat = geom.coordinates.flat(2);
      const lats = flat.map(c => c[1]);
      const lngs = flat.map(c => c[0]);
      return [
        (Math.min(...lats) + Math.max(...lats)) / 2,
        (Math.min(...lngs) + Math.max(...lngs)) / 2,
      ];
    }

    return null;
  } catch {
    return null;
  }
}

export default function SectorMapView({
  geoData,
  mapCenter,
  usingUkFallback = false,
  onSectorClick,
  highlightSector = null,
  searchHighlight = null,
  outlineColor = "red",
  activeColor = "rgba(255,0,0,0.55)",
  authId = null,
  setRegionOverride,
  normalizedRegion,
  isMobile = false,
}) {
  const extractSectorName = (props) =>
    props?.name ||
    props?.sector ||
    props?.postcode ||
    props?.label ||
    props?.pcd_sector ||
    props?.pcd ||
    props?.pcd2 ||
    props?.pcd_sect ||
    props?.pcd_sector_code ||
    "";

  const baseStyle = {
    fillColor: "rgba(255,0,0,0.15)",
    weight: 1.5,
    opacity: 1,
    color: outlineColor,
    fillOpacity: 0.25,
  };

  const activeStyle = {
    fillColor: activeColor,
    weight: 3,
    color: outlineColor,
    fillOpacity: 0.65,
  };

  const searchStyle = {
    fillColor: "rgba(255,0,0,0.30)",
    weight: 2,
    color: outlineColor,
    fillOpacity: 0.45,
  };

  const getSectorStyle = (feature) => {
    const rawName = extractSectorName(feature.properties);
    const sectorKey = normalizeSectorName(rawName);

    const isClicked =
      highlightSector &&
      normalizeSectorName(highlightSector) === sectorKey;

    const isSearch =
      searchHighlight &&
      normalizeSectorName(searchHighlight) === sectorKey;

    if (isClicked) return activeStyle;
    if (isSearch) return searchStyle;
    return baseStyle;
  };

  const onEachFeature = (feature, layer) => {
    const rawName = extractSectorName(feature.properties);
    const sectorKey = normalizeSectorName(rawName);

    if (!sectorKey) return;

    layer.on("click", (e) => {
      L.DomEvent.stopPropagation(e);

      const center = getFeatureCenter(feature);

      const sectorObj = {
        name: sectorKey,
        center,
        ...feature.properties,
      };

      onSectorClick?.(sectorKey, sectorObj);
    });
  };

  return (
    <MapContainer
      key={mapCenter.join(",")}
      center={mapCenter}
      zoom={10}
      scrollWheelZoom
      style={{
        height: "100%",
        width: "100%",
        position: "relative",
        zIndex: 1,
      }}
      whenCreated={(map) => {
        window.__leafletMap = map;
      }}
    >
      <TileLayer
        attribution="© Stadia Maps © OpenStreetMap contributors"
        url="https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}.png"
      />

      {geoData && (
        <GeoJSON
          data={geoData}
          style={getSectorStyle}
          onEachFeature={onEachFeature}
        />
      )}
    </MapContainer>
  );
}
